// apps/web/src/routes/$lang/_layout/stays.$city.$slug.index.tsx
import { createFileRoute, notFound } from "@tanstack/react-router";
import { isApiError } from "@majourneys/api-client";
import { StayDetailPage } from "../../../features/stays/pages/stay-detail-page";
import { stayPropertyQueryOptions } from "../../../features/stays/api/stays-query";
import { parseChildAges } from "../../../features/stays/lib/nights";
import { hasBrowserSession } from "../../../lib/auth";

export interface StaysDetailSearch {
  checkin?: string;
  checkout?: string;
  adults?: number;
  /**
   * Comma separated child ages, one per child. Declared here even though it is
   * only passed through: an UNdeclared param reaches the page in whatever
   * shape the router guesses, and "childAges=5" arrived as the number 5, which
   * crashed the page on .split(). Declaring it pins the type.
   */
  childAges?: string;
}

/**
 * checkin/checkout/adults mirror `stayPropertyListQuerySchema` in
 * packages/shared/src/schemas/stay-properties.ts — the API only prices when
 * all three are present, so a partial set is left partial here too rather
 * than coerced into a full one. Same validateSearch as
 * stays.$city.index.tsx, by design: navigating from the city list to a
 * property must carry the guest's dates through.
 */
export const Route = createFileRoute("/$lang/_layout/stays/$city/$slug/")({
  validateSearch: (search: Record<string, unknown>): StaysDetailSearch => {
    const checkin = typeof search.checkin === "string" ? search.checkin : undefined;
    const checkout = typeof search.checkout === "string" ? search.checkout : undefined;
    const adultsRaw = search.adults;
    const adults =
      typeof adultsRaw === "number"
        ? adultsRaw
        : typeof adultsRaw === "string" && adultsRaw.trim() !== ""
          ? Number(adultsRaw)
          : undefined;
    const childAges =
      typeof search.childAges === "string"
        ? search.childAges
        : typeof search.childAges === "number"
          ? String(search.childAges)
          : undefined;
    return {
      ...(checkin ? { checkin } : {}),
      ...(childAges ? { childAges } : {}),
      ...(checkout ? { checkout } : {}),
      ...(adults !== undefined && Number.isFinite(adults) ? { adults } : {}),
    };
  },
  // Fetch BEFORE the server renders. Without this the SSR pass ships
  // "Loading this stay..." to crawlers and the page's own writing, which is
  // the whole reason this catalogue is curated, never reaches the index.
  /**
   * childAges is part of the query key (see stayKeys.property), so omitting it
   * here warmed a childless key while the page asked for one carrying the
   * children: the SSR-fetched entry was never read and the client repeated the
   * supplier call, which is a paid lookup and a total that visibly changes
   * after the page has painted.
   *
   * Parsed to the page's own shape — undefined, never [], when there are no
   * children, because the key stringifies [] as "" and undefined as null.
   */
  loaderDeps: ({ search }: { search: StaysDetailSearch }) => ({
    checkin: search.checkin,
    checkout: search.checkout,
    adults: search.adults,
    childAges: search.childAges ? parseChildAges(search.childAges) : undefined,
  }),
  // A removed property must answer 404, not 500. The uncaught API 404 used to
  // surface as a server error, which Google treats as the site being down and
  // keeps re-crawling instead of dropping the URL.
  loader: async ({ context, params, deps }) => {
    const options = stayPropertyQueryOptions(params.slug, params.lang, deps);

    /**
     * Block ONLY during SSR. `deps` carries checkin/checkout/adults, so the
     * router re-runs this loader on every date change — and awaiting a paid
     * supplier price call here froze the whole page for the seconds it took,
     * so a guest picking a check-out date watched the calendar sit on the old
     * selection until the quote came back.
     *
     * On the server the await stays: crawlers and the first paint must get the
     * priced page (see loaderDeps above). On the client the fetch is fired but
     * not awaited, so the navigation — and the calendar highlight — commits
     * instantly, and the page's own `useStayProperty` query renders its price
     * loading state (`mj-sweep`) while the supplier answers.
     */
    if (typeof window === "undefined") {
      try {
        await context.queryClient.ensureQueryData(options);
      } catch (error) {
        if (isApiError(error) && error.status === 404) throw notFound();
        throw error;
      }
    } else if (!context.hasSessionHint && !hasBrowserSession()) {
      /**
       * Skipped for a signed-in guest: this warms the PUBLIC entry, which a
       * member never reads (their page keys on their user id), so it would be
       * a second paid supplier call on every date change. `hasSessionHint`
       * covers the hydration pass; after that the root recomputes it as false
       * on the client, so the auth store is checked too.
       */
      void context.queryClient.ensureQueryData(options);
    }
  },
  component: StayDetailPage,
});
